// IssuesCard — the open-issues table plus the lifetime raised/closed line.
//
// Unlike the milestones section, `totalCount` here IS the honest denominator
// for the table: it counts open issues, which is exactly what the rows list.
// A truncation note can therefore quote it, and does.

import MetricCard from "../tracking/deliverable/MetricCard";
import TrackingStatusBadge from "../tracking/shared/TrackingStatusBadge";
import IssueSeverity from "../tracking/issue/IssueSeverity";
import ReportSection, {
  NothingToReport,
  UnavailableNotice,
} from "./ReportSection";
import { countTone } from "./reportFormat";
import type {
  SectionUnavailable,
  SummaryIssueRow,
  SummaryIssuesSection,
} from "../../../types/report";

const SECTION_LABEL = "Issues";

export interface IssuesCardProps {
  section: SummaryIssuesSection | SectionUnavailable;
}

export default function IssuesCard({ section }: IssuesCardProps) {
  if (section.status === "unavailable") {
    return (
      <ReportSection id="issues" title={SECTION_LABEL}>
        <UnavailableNotice label={SECTION_LABEL} />
      </ReportSection>
    );
  }

  if (section.lifetimeRaised === 0) {
    return (
      <ReportSection id="issues" title={SECTION_LABEL}>
        <NothingToReport />
      </ReportSection>
    );
  }

  return (
    <ReportSection id="issues" title={SECTION_LABEL}>
      <div
        data-testid="issues-metrics"
        className="grid grid-cols-1 gap-3 sm:grid-cols-3"
      >
        <MetricCard
          testId="issues-open"
          label="Open"
          value={section.openCount}
          valueTone={countTone(section.openCount, "warning")}
        />
        <MetricCard
          testId="issues-critical"
          label="Critical open"
          value={section.criticalOpenCount}
          valueTone={countTone(section.criticalOpenCount, "danger")}
        />
        <MetricCard
          testId="issues-overdue-actions"
          label="Overdue actions"
          value={section.overdueActionCount}
          valueTone={countTone(section.overdueActionCount, "danger")}
        />
      </div>

      <p
        data-testid="issues-lifetime"
        className="mt-3 text-caption tabular-nums text-slate-500 dark:text-slate-400"
      >
        {section.lifetimeRaised} issues raised over the life of the contract,{" "}
        {section.lifetimeClosed} closed
      </p>

      {section.rows.length === 0 ? (
        <p className="mt-4 text-body-sm text-slate-500 dark:text-slate-400">
          No open issues
        </p>
      ) : (
        <>
          <div
            className="mt-4 overflow-x-auto"
            tabIndex={0}
            role="group"
            aria-label="Open issues"
          >
            <table className="w-full">
              <caption className="sr-only">Open issues</caption>
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                  <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                    Ref
                  </th>
                  <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                    Issue
                  </th>
                  <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                    Severity
                  </th>
                  <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                    Party
                  </th>
                  <th className="px-3 py-2 text-right text-overline uppercase text-slate-600 dark:text-slate-400">
                    Age
                  </th>
                </tr>
              </thead>
              <tbody>
                {section.rows.map((row) => (
                  <IssueRow key={row.id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
          {section.truncated && (
            <p
              data-testid="issues-truncation-note"
              className="mt-2 text-caption text-slate-500 dark:text-slate-400"
            >
              Showing {section.rows.length} of {section.totalCount} open issues
            </p>
          )}
        </>
      )}
    </ReportSection>
  );
}

function IssueRow({ row }: { row: SummaryIssueRow }) {
  return (
    <tr className="border-b border-slate-100 dark:border-slate-700">
      <td
        data-testid={`issue-ref-${row.id}`}
        className="px-3 py-2 font-mono text-body-sm text-slate-500 dark:text-slate-400"
      >
        {row.issueRef === null ? "—" : row.issueRef}
      </td>
      <td className="px-3 py-2 text-body-sm text-slate-900 dark:text-slate-100">
        {row.title}
      </td>
      <td data-testid={`issue-severity-${row.id}`} className="px-3 py-2">
        {row.severity === null ? (
          <span className="text-body-sm text-slate-500 dark:text-slate-400">
            —
          </span>
        ) : (
          <IssueSeverity severity={row.severity} />
        )}
      </td>
      <td data-testid={`issue-status-${row.id}`} className="px-3 py-2">
        {row.issueStatus === null ? (
          <span className="text-body-sm text-slate-500 dark:text-slate-400">
            —
          </span>
        ) : (
          <TrackingStatusBadge kind="issue" status={row.issueStatus} />
        )}
      </td>
      <td
        data-testid={`issue-party-${row.id}`}
        className="px-3 py-2 text-body-sm text-slate-900 dark:text-slate-100"
      >
        {row.party === null ? "—" : row.party}
      </td>
      <td
        data-testid={`issue-age-${row.id}`}
        className="px-3 py-2 text-right text-body-sm tabular-nums text-slate-900 dark:text-slate-100"
      >
        {row.ageDays} {row.ageDays === 1 ? "day" : "days"}
      </td>
    </tr>
  );
}
